import { z } from 'zod';

export const filterLabel = (filter: string): string =>
  ({
    all: 'Todos',
    current: 'Atual',
    deprecated: 'Em convivência',
    'release-candidate': 'Versão candidata',
    'finnest-as-is': 'Finnest atual',
    mockopin: 'MockOPIN',
    'power-admin-customer': 'Power admin/cliente',
    'opin-provider-canonical': 'OPIN oficial',
    'finnest-receiver-workflow': 'Receptora atual',
    'mockopin-harness': 'Suíte MockOPIN',
    'cross-cutting': 'Transversal',
  })[filter] ?? `Fase ${filter}`;

export const statusLabel = (token: string): string =>
  ({
    implemented: 'implementado',
    missing: 'faltando',
    partial: 'parcial',
    blocked: 'bloqueado',
    'receiver-only': 'apenas receptora',
    supported: 'suportado',
    variant: 'variante',
    'version-variant': 'variante de versão',
    'not-applicable': 'não se aplica',
    clear: 'sem pendências',
    warning: 'atenção',
    error: 'erro',
    passed: 'aprovado',
    failed: 'falhou',
    'not-run': 'não executado',
    'not-configured': 'não configurado',
    running: 'executando',
    ready: 'pronto',
    seeded: 'com dados de teste',
    unknown: 'desconhecido',
  })[token] ?? token;

const QUOTE_PREFIX = 'Quote ';
const OPEN_DATA_PREFIX = 'Open Data — ';

// The OPIN product lines and the other API families the catalog names; a family the generated
// catalog adds later shows its catalog name until it is labelled here (`labels.test.ts`).
const OpinProduct = z.enum([
  'Acceptance And Branches Abroad',
  'Assistance General Assets',
  'Auto',
  'Auto Extended Warranty',
  'Auto Insurance',
  'Business',
  'Capitalization Title',
  'Condominium',
  'Cyber Risk',
  'Directors & Officers Liability',
  'Domestic Credit',
  'Engineering',
  'Environmental Liability',
  'Equipment Breakdown',
  'Errors & Omissions Liability',
  'Export Credit',
  'Extended Warranty',
  'Financial Risk',
  'General Liability',
  'Home Insurance',
  'Housing',
  'Life Pension',
  'Lost Profit',
  'Patrimonial',
  'Pension Plan',
  'Person',
  'Private Guarantee',
  'Public Guarantee',
  'Rent Guarantee',
  'Responsibility',
  'Rural',
  'Stop Loss',
  'Transport',
]);
type OpinProduct = z.infer<typeof OpinProduct>;

const OpinApiFamily = z.enum([
  'Admin (Metrics)',
  'Claim Notification',
  'Comuns (Status & Outages)',
  'Consents',
  'Contract Life Pension',
  'Customers',
  'Data Channels',
  'Dynamic Fields',
  'Endorsement',
  'Financial Assistance',
  'Idempotency (Idempotência)',
  'Intermediary',
  'Notifications',
  'Power Admin OPIN Governance',
  'Referenced Network',
  'Resources',
  'Versioning Policy (Multiversionamento)',
  'Webhook',
  'Withdrawal',
]);
type OpinApiFamily = z.infer<typeof OpinApiFamily>;

const OPIN_PRODUCT_LABEL = {
  'Acceptance And Branches Abroad': 'Aceitação e sucursal no exterior',
  'Assistance General Assets': 'Assistência a bens em geral',
  Auto: 'Automóvel',
  'Auto Extended Warranty': 'Garantia estendida de automóvel',
  'Auto Insurance': 'Seguro de automóvel',
  Business: 'Empresarial',
  'Capitalization Title': 'Título de capitalização',
  Condominium: 'Condomínio',
  'Cyber Risk': 'Risco cibernético',
  'Directors & Officers Liability': 'Responsabilidade civil de administradores (D&O)',
  'Domestic Credit': 'Crédito interno',
  Engineering: 'Riscos de engenharia',
  'Environmental Liability': 'Responsabilidade civil ambiental',
  'Equipment Breakdown': 'Quebra de máquinas',
  'Errors & Omissions Liability': 'Responsabilidade civil profissional (E&O)',
  'Export Credit': 'Crédito à exportação',
  'Extended Warranty': 'Garantia estendida',
  'Financial Risk': 'Riscos financeiros',
  'General Liability': 'Responsabilidade civil geral',
  'Home Insurance': 'Seguro residencial',
  Housing: 'Habitacional',
  'Life Pension': 'Vida e previdência',
  'Lost Profit': 'Lucros cessantes',
  Patrimonial: 'Patrimonial',
  'Pension Plan': 'Plano de previdência',
  Person: 'Pessoas',
  'Private Guarantee': 'Garantia de obrigações privadas',
  'Public Guarantee': 'Garantia de obrigações públicas',
  'Rent Guarantee': 'Fiança locatícia',
  Responsibility: 'Responsabilidade',
  Rural: 'Rural',
  'Stop Loss': 'Excesso de danos (stop loss)',
  Transport: 'Transportes',
} as const satisfies Record<OpinProduct, string>;

const OPIN_API_FAMILY_LABEL = {
  'Admin (Metrics)': 'Administração (métricas)',
  'Claim Notification': 'Aviso de sinistro',
  'Comuns (Status & Outages)': 'Comuns (status e indisponibilidades)',
  Consents: 'Consentimentos',
  'Contract Life Pension': 'Contratação de vida e previdência',
  Customers: 'Dados cadastrais',
  'Data Channels': 'Canais de atendimento',
  'Dynamic Fields': 'Campos dinâmicos',
  Endorsement: 'Endosso',
  'Financial Assistance': 'Assistência financeira',
  'Idempotency (Idempotência)': 'Idempotência',
  Intermediary: 'Intermediários',
  Notifications: 'Notificações',
  'Power Admin OPIN Governance': 'Governança OPIN do Power Admin',
  'Referenced Network': 'Rede referenciada',
  Resources: 'Recursos',
  'Versioning Policy (Multiversionamento)': 'Multiversionamento',
  Webhook: 'Webhook',
  Withdrawal: 'Resgate',
} as const satisfies Record<OpinApiFamily, string>;

// A product's pt-BR name after `prefix`, or `otherwise` for a product the catalog adds later.
const productLabel = (product: string, prefix: string, otherwise: string): string => {
  const known = OpinProduct.safeParse(product);
  return known.success ? `${prefix}${OPIN_PRODUCT_LABEL[known.data]}` : otherwise;
};

// A family the official `current` moved to a new major is named with that major: `Data Channels (v3)`.
const NEW_MAJOR = /^(?<base>.+) \((?<major>v\d+)\)$/u;

/** An OPIN catalog family as the operator reads it: products, open data and quotes in pt-BR. */
export const familyLabel = (family: string): string => {
  const newMajor = NEW_MAJOR.exec(family)?.groups;
  const base = newMajor?.['base'];
  const major = newMajor?.['major'];
  if (base !== undefined && major !== undefined) return `${familyLabel(base)} (${major})`;
  const apiFamily = OpinApiFamily.safeParse(family);
  if (apiFamily.success) return OPIN_API_FAMILY_LABEL[apiFamily.data];
  if (family.startsWith(OPEN_DATA_PREFIX)) {
    return productLabel(family.slice(OPEN_DATA_PREFIX.length), 'Dados abertos — ', family);
  }
  if (family.startsWith(QUOTE_PREFIX)) {
    return productLabel(family.slice(QUOTE_PREFIX.length), 'Cotação — ', family);
  }
  return productLabel(family, '', family);
};
